import { useCallback, useState, type ReactNode } from 'react';
import { useT } from '../i18n';

export type Dir = 1 | -1;
type Cmp<Row> = (a: Row, b: Row) => number;

/** Numbers and money sort with the blanks last, whichever way the column runs. */
export const byNumber =
  <Row,>(f: (r: Row) => number | null | undefined): Cmp<Row> =>
  (a, b) => {
    const x = f(a);
    const y = f(b);
    if (x === null || x === undefined) return y === null || y === undefined ? 0 : Number.POSITIVE_INFINITY;
    if (y === null || y === undefined) return Number.NEGATIVE_INFINITY;
    return x - y;
  };
export const byText =
  <Row,>(f: (r: Row) => string | null | undefined): Cmp<Row> =>
  (a, b) => {
    const x = f(a) ?? '';
    const y = f(b) ?? '';
    if (x === '') return y === '' ? 0 : Number.POSITIVE_INFINITY;
    if (y === '') return Number.NEGATIVE_INFINITY;
    return x.localeCompare(y, undefined, { numeric: true, sensitivity: 'base' });
  };

/**
 * A heading that sorts the sheet: a click sorts by it, a second click turns it around. Blanks stay at the
 * bottom both ways. `descFirst` names the columns that read best biggest first (money, dates).
 */
export function useSort<Row, K extends string>(sorters: Record<K, Cmp<Row>>, initial: { key: K; dir: Dir }, descFirst: readonly K[] = []) {
  const t = useT();
  const [sort, setSort] = useState(initial);
  const sorted = useCallback(
    (rows: readonly Row[]): Row[] => {
      // a heading that went away (a tier switched off) leaves the rows in the order they came
      const cmp = sorters[sort.key] ?? (() => 0);
      return [...rows].sort((a, b) => {
        const c = cmp(a, b);
        if (Number.isNaN(c)) return 0;
        // an infinite result marks a blank: it goes last either way
        if (!Number.isFinite(c)) return c > 0 ? 1 : -1;
        return c * sort.dir;
      });
    },
    [sorters, sort],
  );
  const head = (key: K, label: string): ReactNode => (
    <button
      type="button"
      className="th-sort"
      onClick={() => setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: descFirst.includes(key) ? -1 : 1 }))}
      aria-label={sort.key === key ? `${label}, ${sort.dir === 1 ? t('sortedAsc') : t('sortedDesc')}` : `${t('sortBy')}: ${label}`}
    >
      {label}
      <span className="sort-mark" aria-hidden="true">
        {sort.key === key ? (sort.dir === 1 ? '▲' : '▼') : ''}
      </span>
    </button>
  );
  return { sort, sorted, head };
}
